import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@inertiajs/react';
import {
  Bone, Bug, Calculator, Droplet, Fan, Gavel, HeartHandshake, House, Scale,
  Smile, Sparkles, Sprout, Zap,
} from 'lucide-react';
import { Seo, softwareApplication } from '~/marketing/Seo';
import { Card, Badge, StatCard, Button } from '~/marketing/ui';
import { Stat } from '~/marketing/Stat';
import { routes } from '~/marketing/routes';
import { withMarketingLayout } from '~/marketing/MarketingLayout';
import { fmt, quoted } from '~/marketing/marketingData';
import { PostCard } from '~/marketing/PostCard';
import { latestPosts, BENCHMARK_POST } from '~/marketing/blog';
import industries from '~/marketing/industries.json';

// Alphabetical, the founder's call 2026-10-14, so home services and professions
// mix rather than every trade coming first in the order they were measured.
const CARDS = [...industries].sort((a, b) => a.name.localeCompare(b.name));
import { findingFor } from '~/marketing/industryFindings';

/* Replaces pages/Home.jsx wholesale. Serves GET /. */

const BANNER = ['hvac_named', 'plumbing_field', 'family_law_named', 'chiropractic_advice', 'all_industries'];

/* What a plan does, stated in the units the product is actually sold in:
   businesses tracked, questions asked, engines covered. Nothing here is a
   figure we cannot show — the three samples and the weekly cadence are the
   collection design, and the five engines are the five adapters. */
const PLAN_FEATURES = [
  { k: 'Every week', h: 'Enough readings to draw a line',
    b: 'The same twenty questions, asked three times of each AI assistant, every week. Two points make a line and the third tells you whether the line is real.' },
  { k: 'Five AI assistants', h: 'ChatGPT, Claude, Gemini, Perplexity and Google',
    b: 'All five on one schedule, so comparing them always compares the same week. Google is run as a real search from your city.' },
  // "Most tools report one number for both" went 2026-10-11. It is a claim
  // about other people's products that a reader cannot check, on the page
  // arguing that our figures can be — and the card makes its point without it.
  { k: 'Named and cited', h: 'Two measurements, because they need two fixes',
    b: 'Being recommended depends on your reputation; being cited depends on your content. We count them separately.' },
  // "Who won instead" read as though the reader is never named, and "it is
  // rarely the set a business would have named itself" was never measured.
  { k: 'Who else is named', h: 'The competitors AI assistants name',
    b: 'Found in the answers themselves, so the list is whoever AI assistants recommend in your city.' },
];

/* The competitive differences. Each is a recorded decision with evidence:
   local targeting (docs/decisions/0003), the two denominators (Visibility and
   Citedness), the published exclusions, and discovered competitors. */
const DIFFERENCES = [
  { h: 'Measured from your city, not your country',
    b: 'Google is a search run from the place you are tracking, resolved against the provider’s own list of 26,544 US locations. A national average cannot tell a Boise plumber anything about Boise.' },
  { h: 'The denominator is published',
    b: 'Your figure counts only answers that recommended a business. The answers left out, and why, are listed beside it.' },
  // "Twenty questions, not one" contrasted with a choice nobody made, and "which
  // sources get cited" is not a kind of question. Reworded 2026-10-26.
  { h: 'Twenty questions, two kinds',
    b: 'Ten ask who to hire, and your figure is built from those. The other ten ask how to choose and what things cost, and their answers reference different websites. Of every website referenced in the answers we collected by September 2026, just over half appeared only in answers to these ten.' },
  // "You can check the arithmetic" suggested the arithmetic might be wrong, and
  // the methodology page publishes no model versions. Reworded 2026-10-26.
  { h: 'Every figure shows where it came from',
    b: 'Every figure carries its date, its denominator and the AI assistants behind it, and the full method is on the methodology page.' },
];


/* ILLUSTRATIVE, not measured: invented businesses and invented counts, shown
   only under the "Illustrative sample" tag. The hero is a replay, never an
   input — the only company-name field on the site is the real grader. */
/* **Northgate leads, decided 2026-10-11.** The card defaulted to Valley Air —
   3 of 14, with three of the five engines reading "Not named" — so a visitor's
   first sight of what this product returns was a business mostly absent, which
   reads as the report failing rather than the business. That is the complaint
   this file recorded on 2026-09-25 about the illustrative sample, still true of
   what was shown first.

   **The weak cases stay, one click away and unchanged.** Leading with the
   strong one shows the report at its most legible — five rows with findings in
   them, competitors, a figure worth reading — rather than claiming a result.
   The card still says it is built with invented businesses.

   `prerender_test` pins the first entry's strings by name on purpose, so
   reordering this array is supposed to send somebody to that file. */
const SAMPLES = [
  { tab: 'Northgate Electric · Akron', industry: 'Electrical', prompt: 'Best electrician in Akron for a panel upgrade?', date: '18 Jun 2026', named: 9, total: 15,
    caption: 'Named by all 5 AI assistants.', competitors: ['Buckeye Electrical', 'Summit Power Co.'],
    rows: [
      ['ChatGPT', 'signal', 'Cited', 'Cited with a link in 2 of 3 answers.'],
      ['Claude', 'signal', 'Cited', 'Cited in 2 of 3 answers.'],
      ['Gemini', 'signal', 'Cited', 'Named in 2 of 3 answers, twice as the first business named.'],
      ['Perplexity', 'data', 'Named', 'Named in 1 of 3 answers, no link.'],
      ['Google', 'signal', 'Cited', 'An AI Overview appeared in all 3 searches. Named in 2 of them.'],
    ] },
  { tab: 'Valley Air · Fresno', industry: 'HVAC', prompt: 'Who is the best AC repair company in Fresno?', date: '12 Jun 2026', named: 3, total: 14,
    caption: 'Named by 2 of the 5 AI assistants.', competitors: ['Sierra Comfort Air', 'Donley Service', 'Bell Brothers', 'Yelp · top 10 list'],
    rows: [
      ['ChatGPT', 'neutral', 'Not named', 'Not named in any of the 3 answers. Four other businesses named instead.'],
      ['Claude', 'neutral', 'Not named', 'Not named in any of the 3 answers.'],
      ['Gemini', 'data', 'Named', 'Named in 1 of 3 answers, never with a link.'],
      ['Perplexity', 'signal', 'Cited', 'Cited with a link in 2 of 3 answers, always below a directory.'],
      ['Google', 'neutral', 'Not named', 'An AI Overview appeared in 2 of 3 searches. Not named in either; Google showed no overview for the third.'],
    ] },
  { tab: 'Copper Creek · Boise', industry: 'Plumbing', prompt: 'I need an emergency plumber near me in Boise tonight.', date: '04 Jun 2026', named: 1, total: 14,
    caption: 'Named by 1 of the 5 AI assistants.', competitors: ['Right Now Plumbing', 'Boise Plumbing Co.', 'Angi · directory', 'Thumbtack · directory'],
    rows: [
      ['ChatGPT', 'data', 'Named', 'Named in 1 of 3 answers, never with a link.'],
      ['Claude', 'neutral', 'Not named', 'Not named in any of the 3 answers.'],
      ['Gemini', 'neutral', 'Not named', 'Not named in any of the 3 answers. Three directories cited instead.'],
      ['Perplexity', 'neutral', 'Not named', 'Not named in any of the 3 answers.'],
      ['Google', 'neutral', 'Not named', 'An AI Overview appeared in 2 of 3 searches. Not named in either.'],
    ] },
];

/* **What a plan does, under the nav's name for it**, rewritten 2026-10-26 on the
   founder's review. The section was "How the check works" and described a
   plan: twenty questions, three times each — where the free check asks one
   question once. "Generated" was wrong for the written sets most industries
   have, "who was named instead" read as though the reader never is, and "what
   to change first" promised a ranking nothing computes. */
const STEPS = [
  ['We write your questions', 'Twenty questions for your industry, asked in your city: who to hire, how to choose and what it costs.'],
  ['We ask five AI assistants', 'ChatGPT, Claude, Gemini, Perplexity and Google AI Overviews, through APIs, three times a question, every week.'],
  ['You see where you stand', 'How often you’re recommended, who else is named, and the sites the answers draw on.'],
];
const phTag = {
  fontSize: 10, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--ink-200)',
  border: '1px dashed rgba(255,255,255,.28)', borderRadius: 3, padding: '3px 8px',
  backgroundImage: 'repeating-linear-gradient(135deg,rgba(255,255,255,.07) 0 2px,transparent 2px 7px)',
};
const pill = (on) => ({ fontFamily: 'var(--font-mono)', fontSize: 11, padding: '6px 10px', borderRadius: 'var(--r-full)', cursor: 'pointer', border: '1px solid ' + (on ? 'var(--teal)' : 'var(--border)'), background: on ? 'var(--teal-tint-strong)' : 'transparent', color: on ? 'var(--teal)' : 'var(--text-body)' });
const engineRow = { display: 'grid', gridTemplateColumns: 'minmax(0,104px) minmax(0,1fr) auto', gap: 12, alignItems: 'center', padding: '12px 0', borderTop: '1px solid var(--border-subtle)' };
const small = { fontSize: 'var(--fs-body-sm)', color: 'var(--text-body)' };
// The industry cards' text, a size up from `small`: a square card has room for it.
const finding = { fontSize: 'var(--fs-body)', color: 'var(--text-body)' };

function usePrefersReducedMotion() {
  const [r, setR] = useState(false);
  useEffect(() => { const m = window.matchMedia('(prefers-reduced-motion: reduce)'); setR(m.matches); }, []);
  return r;
}

/* The industry section's heading names each industry in turn, 2026-10-30, from
   a design the founder supplied (a rotating word on a line of its own) — "Be
   the local [plumber] AI recommends", the founder's wording, so each name is
   the singular, and "local" because each business is measured in its own
   city. Built
   without the animation library the design came with: a transform and an
   opacity transition do the same, and a dependency for one heading is not
   worth its weight in a bundle every visitor downloads.

   **All of them share one grid cell, as the banner's figures do**, so the line
   is as tall as the longest name wraps to at every width and the page below
   never moves as it turns. A crawler reads every name. The ones not showing are
   hidden from screen readers. With reduced motion it stays on the first.

   **One line from 1120px up, the founder's call the same day**, with the name's
   own line below that. On one line the cell takes the width of the name
   showing, measured, and eases to the next one's, so "AI recommends" moves
   along beside it instead of leaving a gap the width of the longest name.
   From 1120px the longest sentence (1,055px) fits at the heading's size; narrower,
   a long name would push "AI recommends" onto a second line and a short one
   would not, and the page would move every two seconds. (application.css) */
const INDUSTRY_WORDS = industries.map((i) => i.business);

function IndustryWords({ reduced }) {
  const [n, setN] = useState(0);
  const words = useRef([]);
  const [widths, setWidths] = useState(null);
  useEffect(() => {
    if (reduced) return;
    const id = setTimeout(() => setN((n + 1) % INDUSTRY_WORDS.length), 2000);
    return () => clearTimeout(id);
  }, [n, reduced]);
  // Measured again once the font has loaded and on resize, because a width
  // taken in the fallback face is the wrong width.
  useEffect(() => {
    const measure = () => setWidths(words.current.map((el) => el.offsetWidth));
    measure();
    document.fonts?.ready.then(measure);
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <span className="industry-words" style={widths ? { '--word-width': `${widths[n]}px` } : undefined}>
      {INDUSTRY_WORDS.map((w, i) => (
        <React.Fragment key={w}>
          {i > 0 && ' '}
          <span
            ref={(el) => { words.current[i] = el; }}
            className="industry-words__word"
            aria-hidden={i !== n}
            style={{
              fontWeight: 600, color: 'var(--amber)',
              opacity: i === n ? 1 : 0,
              transform: i === n ? 'none' : `translateY(${i < n ? -110 : 110}%)`,
              transition: reduced ? 'none' : 'transform 700ms cubic-bezier(.34,1.45,.64,1), opacity 350ms ease',
            }}
          >
            {w}
          </span>
        </React.Fragment>
      ))}
    </span>
  );
}

/* The industry cards scroll upward in columns, 2026-10-30, from a second design
   the founder supplied — a wall of cards in slowly moving columns, faded at the
   top and bottom. The founder's call with it: **every card the same width, each
   as tall as its finding needs**, replacing the squares of 2026-10-17.

   **The box is one fixed height from the first paint**, so nothing below it
   moves when the page starts up — "How it works" is an anchor under this
   section, and a box that changed height after loading would move the place
   that link lands, which is the 467px defect of 2026-10-07 again.

   **The second copy of each column is added after the page starts**, so the
   HTML a crawler reads holds every card once, in alphabetical order. That copy
   is hidden from screen readers and skipped by Tab. A column stops while the
   pointer is on it or a card in it has focus, so a moving card can be clicked;
   the design slowed it instead, which still leaves a target moving. With
   reduced motion nothing moves and the box scrolls by hand (application.css).

   One column on a phone, two from 768px, three from 1024px, cut in
   alphabetical runs. The design hid its second and third columns on smaller
   screens, which would take industries off a phone altogether. */
const COLUMN_SPEEDS = [28, 36, 32]; // px a second, a different pace per column

/* Each industry's emblem, in the design's avatar circle — the founder's call,
   2026-10-30, replacing the industry's first letter, which gave three cards a
   "P". Imported from `lucide-react` directly, because the design system's
   `Icon` carries a closed list of 36 and is edited only by export; drawn at
   that component's 1.75 stroke so the two read as one set. Dental is a smile
   because the library has no tooth. An industry added to `industries.json`
   without one fails the build here rather than drawing an empty circle. */
const EMBLEMS = {
  hvac: Fan, plumbing: Droplet, roofing: House, electrical: Zap,
  pest_control: Bug, landscaping: Sprout, accounting: Calculator,
  family_law: Scale, chiropractic: Bone, home_care: HeartHandshake,
  med_spa: Sparkles, pi_law: Gavel, dental: Smile,
};
const unmarked = industries.filter((i) => !EMBLEMS[i.key]).map((i) => i.key);
if (unmarked.length) throw new Error(`Home.jsx: no emblem for ${unmarked.join(', ')}`);

function useColumnCount() {
  const [n, setN] = useState(3);
  useEffect(() => {
    const lg = window.matchMedia('(min-width: 1024px)');
    const md = window.matchMedia('(min-width: 768px)');
    const update = () => setN(lg.matches ? 3 : md.matches ? 2 : 1);
    update();
    lg.addEventListener('change', update);
    md.addEventListener('change', update);
    return () => { lg.removeEventListener('change', update); md.removeEventListener('change', update); };
  }, []);
  return n;
}

// Balanced runs: thirteen in three is 5, 4, 4 rather than 5, 5, 3, so no
// column is too short to fill the box.
function columnsOf(items, n) {
  const base = Math.floor(items.length / n), extra = items.length % n;
  let at = 0;
  return Array.from({ length: n }, (_, i) => {
    const size = base + (i < extra ? 1 : 0);
    const run = items.slice(at, at + size);
    at += size;
    return run;
  });
}

function IndustryCard({ href, body, name, emblem: Emblem, copy = false }) {
  return (
    // No underline: `base.css` underlines every link on hover, which ran
    // through the finding and the name, and the card already marks the hover.
    <Link href={href} tabIndex={copy ? -1 : undefined} style={{ color: 'inherit', textDecoration: 'none', display: 'block', width: 'min(320px, 100%)' }}>
      <Card interactive pad="lg">
        <div style={{ display: 'grid', gap: 20 }}>
          <p style={finding}>{body}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span aria-hidden="true" style={{ flex: 'none', display: 'grid', placeItems: 'center', width: 32, height: 32, borderRadius: 'var(--r-full)', background: 'var(--teal-tint)', color: 'var(--teal)' }}><Emblem size={16} strokeWidth={1.75} /></span>
            <h3 style={{ fontSize: 15, fontWeight: 500, lineHeight: '20px', letterSpacing: 'normal' }}>{name}</h3>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function IndustryColumn({ cards, speed, moving }) {
  const set = useRef(null);
  const [duration, setDuration] = useState(null);
  useEffect(() => {
    if (moving && set.current) setDuration(set.current.offsetHeight / speed);
  }, [moving, speed, cards]);

  const cardsOf = (copy) => cards.map((t) => (
    // No "Questions and findings →" under the name, the founder's call: the
    // whole card is the link, so a line saying so was a second link to read.
    <IndustryCard key={t.key} href={t.path} body={findingFor(t.key)} name={t.name} emblem={EMBLEMS[t.key]} copy={copy} />
  ));

  return (
    <div className="industry-marquee__column">
      <div
        className={'industry-marquee__track' + (moving && duration ? ' industry-marquee__track--moving' : '')}
        style={duration ? { '--marquee-duration': `${duration}s` } : undefined}
      >
        <div ref={set} className="industry-marquee__set">{cardsOf(false)}</div>
        {moving && <div className="industry-marquee__set" aria-hidden="true">{cardsOf(true)}</div>}
      </div>
    </div>
  );
}

function IndustryColumns({ reduced }) {
  const count = useColumnCount();
  const [started, setStarted] = useState(false);
  useEffect(() => setStarted(true), []);
  const moving = started && !reduced;
  const columns = useMemo(() => columnsOf(CARDS, count), [count]);

  return (
    <div className="industry-marquee">
      {columns.map((cards, i) => (
        <IndustryColumn key={`${count}-${i}`} cards={cards} speed={COLUMN_SPEEDS[i]} moving={moving} />
      ))}
    </div>
  );
}

export default function Home() {
  const reduced = usePrefersReducedMotion();
  const [banner, setBanner] = useState(0);
  const [sample, setSample] = useState(0);
  const s = SAMPLES[sample];

  useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => setBanner((b) => (b + 1) % BANNER.length), 5200);
    return () => clearInterval(id);
  }, [reduced]);

  return (
    <>
      <Seo
        title="AI search optimization for local businesses"
        description="See whether ChatGPT, Claude, Gemini, Perplexity and Google AI Overviews name you when customers in your city ask who to hire, and what you can do about it."
        path={routes.home}
        schema={[ softwareApplication ]}
      />

      <section style={{ background: 'var(--bg-deep)', borderBottom: '1px solid var(--border-subtle)' }}>
        <div className="sp-page" style={{ paddingBlock: 'var(--space-8)', display: 'grid', gap: 'var(--space-4)', minHeight: 186, alignContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
            <span className="sp-label">AI search benchmark</span>
            <div style={{ display: 'flex', gap: 6 }}>
              {BANNER.map((id, i) => (
                <button key={id} type="button" aria-label={'Figure ' + (i + 1)} aria-pressed={i === banner} onClick={() => setBanner(i)}
                  style={{ width: 22, height: 4, border: 0, padding: 0, borderRadius: 2, cursor: 'pointer', background: i === banner ? 'var(--amber)' : 'var(--border-strong)' }} />
              ))}
            </div>
          </div>
          {/* **All three figures share one grid cell and only one is visible**,
              2026-10-14, so the banner is always as tall as the tallest and the
              page below it never moves as it rotates — it moved by 26-33px,
              the 467px example report's shape at a smaller size. A crawler
              reads all three, which it could not when only one was rendered. */}
          <div aria-live="polite" style={{ maxWidth: 1000, display: 'grid' }}>
            {BANNER.map((id, i) => (
              <div key={id} aria-hidden={i !== banner} style={{ gridArea: '1 / 1', visibility: i === banner ? 'visible' : 'hidden' }}>
                <Stat id={id} claimSize="clamp(21px,2.5vw,32px)" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <header className="sp-grid-bg" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
        <div className="sp-page" style={{ paddingBlock: 'clamp(48px,6vw,84px)', display: 'grid', gap: 'var(--space-16)', gridTemplateColumns: 'repeat(auto-fit,minmax(min(420px,100%),1fr))', alignItems: 'start' }}>
          <div style={{ display: 'grid', gap: 'var(--space-5)', maxWidth: 620 }}>
            <span className="sp-label">Answer engine optimization · for local businesses</span>
            <h1 style={{ fontSize: 'var(--fs-display-1)', lineHeight: 'var(--lh-display)' }}>When an AI recommends a company in your city, make it <span style={{ color: 'var(--amber)' }}>yours</span>.</h1>
            {/* **Rewritten 2026-10-26 on the founder's review.** "Show you who
                gets named instead of you" read as though the reader is never
                named, so it says how often they are and who else is. "Tell you
                what to change" now says what: the listings and the pages, the
                two groups the dashboard's sources card names — which is why the
                dashboard began naming the sites the same day, when the free
                check already did. Advice, never that we will do it: Managed
                Payments excludes marketing services. */}
            <p style={{ fontSize: 'var(--fs-body-lg)', color: 'var(--text-body)', maxWidth: '56ch' }}>Your customers ask ChatGPT, Claude, Gemini, Perplexity and Google AI Overviews who to hire before they open a search results page. We ask the same questions in your city every week and show you how often you’re recommended, and who else is.</p>
            <p style={{ color: 'var(--text-body)', maxWidth: '56ch' }}>Then we show you what to work on: the directories and review sites the answers draw on, where your business can be listed, and the competitors’ pages they cite, which point to the pages worth adding to your own site.</p>
            {/* Until 2026-10-13 this named four trades as having "their own
                playbook", two with no page; then "its own questions, its own
                competitors and its own sources", which did not say what any of
                the three were. Each now says where it comes from. */}
            <p style={{ color: 'var(--text-body)', maxWidth: '56ch' }}>HVAC, dental, family law or any other local business: the questions are written for your industry, your competitors are the businesses AI assistants name in your city, and the sources are the websites they cite when they answer.</p>
            <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', alignItems: 'center', marginTop: 'var(--space-2)' }}>
              <Button size="lg" href={routes.grader}>Check your visibility</Button>
              <span className="sp-caption">Free. Five AI assistants. No credit card.</span>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span className="sp-mono" style={phTag}>Example report</span>
              <span className="sp-caption">Built with invented businesses, to show the shape of a report.</span>
            </div>
            <Card variant="data">
              <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {SAMPLES.map((x, i) => <button key={x.tab} type="button" aria-pressed={i === sample} onClick={() => setSample(i)} style={pill(i === sample)}>{x.tab}</button>)}
                </div>
                <div style={{ background: 'var(--surface-inset)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', padding: '14px 16px', display: 'grid', gap: 6 }}>
                  <span className="sp-label">Question asked of every AI assistant</span>
                  <span className="sp-mono" style={{ fontSize: 13.5, color: 'var(--text-strong)' }}>“{s.prompt}”</span>
                  <span className="sp-mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>Asked 3 times of each · {s.industry}</span>
                </div>
                {/*
                  **This card is static, and that is the decision rather than
                  the absence of one.** It used to fill over four and a half
                  seconds — each row swapping a one-line "reading answers…" for
                  the real two- and three-line answer, each badge arriving into
                  a grid column sized `auto` so the middle column rewrapped, and
                  the whole summary below mounted at the end. The card grew
                  **467px** as it did, measured at a 1006px viewport, and pushed
                  every section under the hero down by the same amount.

                  Removing the fill rather than stabilising it, 2026-10-07: it
                  simulated work that is not happening, over businesses this
                  card labels invented, on a site whose argument is that its
                  figures can be checked. A visitor needs to see what a report
                  looks like, and that is now simply what is on screen.

                  **Nothing here may become conditional on a timer again
                  without reading `prerender_test`.** Everything in this card is
                  prose a crawler reads, and content that arrives on a timer
                  arrives after every crawler has gone.
                */}
                <div>
                  {s.rows.map(([engine, tone, label, detail]) => (
                    <div key={engine} style={engineRow}>
                      <span className="sp-mono" style={{ fontSize: 13, color: 'var(--text-strong)' }}>{engine}</span>
                      <span style={{ fontSize: 13.5, color: 'var(--text-body)', lineHeight: 1.45 }}>{detail}</span>
                      <Badge tone={tone}>{label}</Badge>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
                  <span className="sp-mono" style={{ fontSize: 12.5, color: 'var(--text-body)' }}>{fmt.named(s.named, s.total)}</span>
                  <StatCard label="Named in" value={s.named} of={s.total} noun="answers" tone="data" size="xl" footer={s.caption} />
                  <div style={{ display: 'grid', gap: 8 }}>
                    <span className="sp-label">Also named</span>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {s.competitors.map((c) => <span key={c} className="sp-mono" style={{ fontSize: 12, padding: '4px 10px', borderRadius: 'var(--r-full)', background: 'rgba(255,255,255,.06)', color: 'var(--ink-200)' }}>{c}</span>)}
                    </div>
                  </div>
                </div>
                {/* The "Replay this sample" button stood here and went with the
                    fill. It replayed an animation over invented data, directly
                    beneath the hero's actual CTA, and took clicks that led
                    nowhere. Its footer label, "Illustrative example · the shape
                    of a real report", went 2026-10-26: the caption above the
                    card says the same and is the card's honesty label now. */}
              </div>
            </Card>
            <p className="sp-caption" style={{ maxWidth: '60ch' }}>Check your own business with the <Link href={routes.grader}>free check</Link>: one real question put to all five AI assistants, with the result emailed to you.</p>
          </div>
        </div>
      </header>

      <section className="sp-page" style={{ paddingBlock: 'var(--section-y)', display: 'grid', gap: 'var(--space-10)' }}>
        {/* **One card per benchmarked industry, all drawn the same**, 2026-10-14.
            It showed HVAC and plumbing as "Live" and electrical and roofing as
            dashed "Next" cards reading "Playbook in progress" — a status about
            our own work, which decays by itself. Each card carries its
            industry's own finding, which its page leads with, so a card cannot
            say what its page does not. */}
        {/* **Centered, with the benchmark above and two ways in below**, from
            the founder's design, 2026-10-30. Its "Jump on a call" became "See
            pricing" — there are no calls anywhere in this funnel — and its
            "Sign up here" the free check, the page's main way in. */}
        <div style={{ display: 'grid', gap: 'var(--space-6)', justifyItems: 'center', textAlign: 'center', paddingBlock: 'var(--space-8)' }}>
          <Button variant="secondary" size="sm" href={BENCHMARK_POST} iconRight="arrow-right">Read the benchmark</Button>
          <div style={{ display: 'grid', gap: 'var(--space-4)', justifyItems: 'center' }}>
            <h2 style={{ fontSize: 'var(--fs-display-2)', lineHeight: 'var(--lh-display)', letterSpacing: 'var(--tr-display)', fontWeight: 400 }}>
              {/* "Be the local" never breaks, the founder's call: on a phone it
                  is the first of three lines. */}
              <span style={{ whiteSpace: 'nowrap' }}>Be the local</span>{' '}
              <IndustryWords reduced={reduced} />
              {' '}AI recommends
            </h2>
            <p style={{ fontSize: 'var(--fs-body-lg)', color: 'var(--text-body)', maxWidth: '60ch' }}>“Who should I call for emergency AC repair?” and “Who is the best divorce attorney?” draw on different businesses and different sources. Here is what we found in some of them.</p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button variant="secondary" size="lg" href={routes.pricing}>See pricing</Button>
            <Button size="lg" href={routes.grader} iconRight="arrow-right">Run the free check</Button>
          </div>
        </div>
        <IndustryColumns reduced={reduced} />
        {/* **Always last, whatever the order above**, 2026-10-14 on the
            founder's call: the answer to "what if I am not on this list?".
            Below the columns now, since a loop has no last card, and a
            sentence with a link rather than a card, the founder's call
            2026-10-30. The free check's "Something else" writes one question
            for any industry, so "the question", singular — a plan writes
            twenty. */}
        <p style={{ textAlign: 'center', color: 'var(--text-body)', maxWidth: '60ch', justifySelf: 'center' }}>
          Not on this list? <Link href={routes.grader}>Run the free check</Link> for any local business, and we’ll write the question your customers ask.
        </p>
      </section>

      <section id="how" style={{ background: 'var(--bg-deep)', borderBlock: '1px solid var(--border-subtle)', scrollMarginTop: 80 }}>
        <div className="sp-page" style={{ paddingBlock: 'var(--section-y)', display: 'grid', gap: 'var(--space-10)' }}>
          <h2 style={{ fontSize: 'var(--fs-h1)', maxWidth: '22ch' }}>How it works</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(260px,100%),1fr))', gap: 'var(--space-6)' }}>
            {STEPS.map(([h, b], i) => (
              <div key={h} style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
                <span className="sp-label">Step {i + 1}/{STEPS.length}</span>
                <h3 style={{ fontSize: 'var(--fs-h4)' }}>{h}</h3>
                <p style={small}>{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sp-page" style={{ paddingBlock: 'var(--section-y)', display: 'grid', gap: 'var(--space-8)' }}>
        <div style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: '62ch' }}>
          <span className="sp-label">Why the number holds up</span>
          <h2 style={{ fontSize: 'var(--fs-h1)' }}>We measure the answer that repeats.</h2>
        </div>
        {/* Three tiles sat here until 2026-10-26 — the variability corpus, and
            words and sources per API answer — with no date and no AI assistant
            named, under a paragraph saying every figure carries both. The
            figures are still on the methodology page, in the sanctioned
            paragraphs that give them context. */}
        <p style={{ color: 'var(--text-body)', maxWidth: '62ch' }}>Ask an AI assistant the same question twice and the answer moves. We collect every answer through an API, three times a question, every week — so what you get is the pattern underneath the variation rather than one lucky answer. That is what makes a trend line mean something, and it is why every figure here carries the date it was measured and the AI assistants behind it. <Link href={routes.methodology}>How we measure</Link>.</p>
      </section>


      {/* What a subscription actually does. Numbers are derived from the plan
          ladder rather than written here: 20 questions x 5 engines x 3 samples,
          weekly. The business count is the unit because a location and a client
          consume identical budget. */}
      <section className="sp-page" style={{ paddingBlock: 'var(--section-y)', display: 'grid', gap: 'var(--space-10)' }}>
        <div style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: '62ch' }}>
          <span className="sp-label">What a plan does</span>
          <h2 style={{ fontSize: 'var(--fs-h1)' }}>One check tells you where you stand. A plan tells you whether it is moving.</h2>
          <p style={{ color: 'var(--text-body)' }}>The same questions in the same city every week, so this week is comparable to last week, and when you publish something new, the readings that follow show whether it worked.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(260px,100%),1fr))', gap: 'var(--space-4)' }}>
          {PLAN_FEATURES.map((f) => (
            <Card key={f.h}>
              <div style={{ display: 'grid', gap: 10 }}>
                <span className="sp-label" style={{ color: 'var(--teal)' }}>{f.k}</span>
                <h3 style={{ fontSize: 'var(--fs-h4)' }}>{f.h}</h3>
                <p style={small}>{f.b}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* The competitive section. Each of these is a decision recorded in the
          repo with evidence behind it, not a marketing claim invented here. */}
      <section style={{ background: 'var(--bg-deep)', borderBlock: '1px solid var(--border-subtle)' }}>
        <div className="sp-page" style={{ paddingBlock: 'var(--section-y)', display: 'grid', gap: 'var(--space-10)' }}>
          <div style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: '62ch' }}>
            <span className="sp-label">Where we differ</span>
            <h2 style={{ fontSize: 'var(--fs-h1)' }}>Four decisions that change what the number means.</h2>
            {/* "Most visibility tools answer a national question with a single score"
                until 2026-10-26: a claim about other products nobody could check, and
                "a score you cannot take apart" said nothing clear. "We have looked
                at" is the competitor research, which found all six measure by country. */}
            <p style={{ color: 'var(--text-body)' }}>The AI visibility tools we have looked at measure a whole country. A local business is chosen city by city, so we measure it in its own city.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(280px,100%),1fr))', gap: 'var(--space-4)' }}>
            {DIFFERENCES.map((d) => (
              <Card key={d.h} variant="data">
                <div style={{ display: 'grid', gap: 10 }}>
                  <h3 style={{ fontSize: 'var(--fs-h4)' }}>{d.h}</h3>
                  <p style={small}>{d.b}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* The trust claim. Per docs/research, none of the six competitors
          documents what happens to collected data when you stop paying — so
          saying it plainly is a differentiator rather than boilerplate.

          **Both sentences are read from `quoted` now rather than typed here.**
          This section carried its own literal of the cancellation string, which
          made it a second copy of a Class 2 string that `copy_rules_test` cannot
          see — that test compares `marketingData.js` against `copy-rules.md` and
          never looks at a page. `withdrawn_copy_test` is the one guard that
          could, and it does: verified on 2026-10-09 by putting the literal back
          and watching it fail this file by name.

          **The retention sentence joins it**, because this is where somebody
          decides whether leaving is survivable and the window is the half that
          answers it. Stating the first without the second is the version of this
          section that was true only while nothing swept. */}
      <section className="sp-page" style={{ paddingBlock: 'var(--section-y)', display: 'grid', gap: 'var(--space-6)' }}>
        <div style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: '62ch' }}>
          <span className="sp-label">If you leave</span>
          <h2 style={{ fontSize: 'var(--fs-h1)' }}>Your history stays yours.</h2>
          <p style={{ color: 'var(--text-body)' }}>{quoted.cancelling}</p>
          <p style={{ color: 'var(--text-body)' }}>{quoted.retention}</p>
          <p style={small}>Try it free for fourteen days, no credit card. Plans are billed monthly, or once a year up front: twelve months for the price of ten. <Link href={routes.pricing}>See the plans</Link>.</p>
        </div>
      </section>

      {/* The newest posts, from the same file the blog lists — so a post the
          blog does not have can never be advertised here. Prerendered with the
          rest of the page, so a crawler reading / finds them. */}
      <section className="sp-page" style={{ paddingBlock: 'var(--section-y)', display: 'grid', gap: 'var(--space-8)' }}>
        <div style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: '62ch' }}>
          <span className="sp-label">From the blog</span>
          <h2 style={{ fontSize: 'var(--fs-h1)' }}>Latest posts</h2>
          <p style={{ color: 'var(--text-body)' }}>Guides to answer engine optimization, and what we find when we measure it. <Link href={routes.blog}>All posts</Link>.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(300px,100%),1fr))', gap: 'var(--space-4)' }}>
          {latestPosts(3).map((post) => <PostCard key={post.path} post={post} headingLevel="h3" />)}
        </div>
      </section>

      <section style={{ background: 'var(--bg-deep)', borderTop: '1px solid var(--border-subtle)' }}>
        <div className="sp-page" style={{ paddingBlock: 'var(--section-y)', display: 'grid', gap: 'var(--space-5)', justifyItems: 'start' }}>
          <h2 style={{ fontSize: 'var(--fs-display-2)', maxWidth: '18ch' }}>See where you stand in your own city.</h2>
          <p style={{ color: 'var(--text-body)', maxWidth: '52ch' }}>Tell us your business name, city, industry and email, and we’ll email you the report.</p>
          <Button size="lg" href={routes.grader}>Run the free check</Button>
        </div>
      </section>
    </>
  );
}

Home.layout = withMarketingLayout;
